/**
 * Validation seams:
 *  - ProfileValidator: L1 checks of `profile.save` (docs/analysis/backend-requirements.md §6); at L2 the server owns the
 *    state (player.command) and the validator sees only imports into a fresh profile;
 *  - AuthoritativeValidator: re-simulates a finished battle headlessly and confirms the result.
 */
import type { CommandLogData } from '../../session/command-log';
import type { BattleInit, BattleResult, ProfileSummary, SaveReason, ValidationVerdict } from '../../shared/protocol/model';

export interface ProfileValidator {
  /** Returns human-readable violations (empty = accepted). */
  validate(input: { previous: ProfileSummary | null; next: ProfileSummary; reason: SaveReason; revision: number }): string[];
}

const MONOTONIC: readonly (keyof ProfileSummary)[] = ['exp', 'careerMax', 'missionsMax', 'survivalMax', 'bossMax', 'assaults'];

/**
 * L1 validator: legacy-format ranges and monotonic progress (except for `reset` and `import`). At L2 it checks the
 * only saves that level accepts: imports into a fresh profile (ranges only, `import` skips the monotonic checks).
 * // UNVERIFIED: finer invariants (perk levels vs max_lim, weapon copy limits, earn-rate limits) need the
 * // game DEF tables in src/shared and are not implemented yet.
 */
export class BasicProfileValidator implements ProfileValidator {
  validate({ previous, next, reason }: { previous: ProfileSummary | null; next: ProfileSummary; reason: SaveReason }): string[] {
    const v: string[] = [];
    if (next.worms > 3) v.push(`worms ${next.worms} exceeds 3`);
    if (next.spec > 4) v.push(`spec ${next.spec} exceeds 4`);
    if (next.careerMax > 3599 || next.missionsMax > 3599 || next.survivalMax > 3599 || next.bossMax > 3599) v.push('stage maximum exceeds 3599');
    if (previous && reason !== 'reset' && reason !== 'import') {
      for (const k of MONOTONIC) {
        if (next[k] < previous[k]) v.push(`${k} decreased from ${previous[k]} to ${next[k]}`);
      }
    }
    return v;
  }
}

export interface BattleValidationInput {
  sessionId: string;
  init: BattleInit;
  seed: number;
  log: CommandLogData;
  claimed: BattleResult;
  finalHash: string | null;
  checkpoints: { tick: number; hash: string }[];
}

export interface BattleValidationVerdict {
  verdict: ValidationVerdict;
  reason: string | null;
  /** Result computed by the validator (when it re-simulated the battle). */
  result?: BattleResult;
  finalHash?: string;
}

export interface AuthoritativeValidator {
  readonly name: string;
  validate(input: BattleValidationInput): Promise<BattleValidationVerdict>;
}

/** Why a server has no battle validator (the `reason` of every verdict it gives). */
export type DisabledValidatorReason = 'validator_disabled' | 'no_game_assets';

/**
 * The validator of a backend that does not re-simulate battles: the in-process (offline) backend, a server started
 * with VALIDATOR=stub ('validator_disabled') or one without the game assets under VALIDATOR=auto ('no_game_assets').
 * Every battle it sees stays 'unverified' with that reason (SessionOutcome.reason); the claim of the participants is
 * applied. With the assets, the self-hosted server re-simulates battles instead (src/server/node/headless-validator.ts
 * and the worker-thread simulator src/server/node/sim-pool.ts).
 */
export class DisabledValidator implements AuthoritativeValidator {
  readonly name = 'disabled';
  constructor(readonly reason: DisabledValidatorReason = 'validator_disabled') {}
  async validate(): Promise<BattleValidationVerdict> {
    return { verdict: 'unverified', reason: this.reason };
  }
}
